import React, { useEffect, useState } from "react";
import { MapControl, ControlPosition, useMap } from "@vis.gl/react-google-maps";
import "./MapNavigationControls.scss";

const PAN_STEP_PX = 80;

const EnterFullscreenIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M1 5V1H5M11 1H15V5M15 11V15H11M5 15H1V11" stroke="#565656" strokeWidth="1.5" fill="none" />
  </svg>
);

const ExitFullscreenIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M5 1V5H1M15 5H11V1M11 15V11H15M1 11H5V15" stroke="#565656" strokeWidth="1.5" fill="none" />
  </svg>
);

const PanIcon = () => (
  <svg width="18" height="18" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <circle cx="8" cy="8" r="1.4" fill="#565656" />
    <polygon points="8,1 5.5,4.5 10.5,4.5" fill="#565656" />
    <polygon points="8,15 5.5,11.5 10.5,11.5" fill="#565656" />
    <polygon points="1,8 4.5,5.5 4.5,10.5" fill="#565656" />
    <polygon points="15,8 11.5,5.5 11.5,10.5" fill="#565656" />
  </svg>
);

const ArrowIcon = ({ direction }) => {
  const points = {
    up: "8,4 4,10 12,10",
    down: "8,12 4,6 12,6",
    left: "4,8 10,4 10,12",
    right: "12,8 6,4 6,12",
  }[direction];

  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <polygon points={points} fill="#565656" />
    </svg>
  );
};

const PlusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M8 3v10M3 8h10" stroke="#565656" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const MinusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M3 8h10" stroke="#565656" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const MapIconButton = ({ onClick, ariaLabel, className = "", children }) => (
  <button
    type="button"
    className={`MapControlButton ${className}`}
    onClick={onClick}
    aria-label={ariaLabel}
  >
    {children}
  </button>
);

// Custom replacement for Google's native fullscreen control. The map's
// default UI controls were rendering duplicated (see BaseGoogleMapReact.js
// for the diagnosis); rendering our own avoids relying on Google's
// control-creation code at all, and gives full control over the styling.
export const CustomFullscreenControl = () => {
  const map = useMap();
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handleChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handleChange);
    return () => document.removeEventListener("fullscreenchange", handleChange);
  }, []);

  const toggleFullscreen = () => {
    if (!map) return;
    const container = map.getDiv();

    if (!document.fullscreenElement) {
      (container.requestFullscreen || container.webkitRequestFullscreen)?.call(
        container
      );
    } else {
      (document.exitFullscreen || document.webkitExitFullscreen)?.call(
        document
      );
    }
  };

  return (
    <MapControl position={ControlPosition.RIGHT_TOP}>
      <MapIconButton
        onClick={toggleFullscreen}
        ariaLabel={isFullscreen ? "Exit fullscreen" : "View fullscreen"}
        className="MapControlButton--fullscreen"
      >
        {isFullscreen ? <ExitFullscreenIcon /> : <EnterFullscreenIcon />}
      </MapIconButton>
    </MapControl>
  );
};

// Custom replacement for Google's native camera control (the joystick-style
// widget that expands into pan/zoom buttons).
export const CustomCameraControl = () => {
  const map = useMap();
  const [expanded, setExpanded] = useState(false);

  const pan = (dx, dy) => map && map.panBy(dx, dy);
  const zoomBy = (delta) =>
    map && map.setZoom((map.getZoom() || 0) + delta);

  return (
    <MapControl position={ControlPosition.RIGHT_BOTTOM}>
      <div className="MapCameraControl">
        {expanded && (
          <div className="MapCameraControlPanel">
            <div className="MapCameraControlDpad">
              <MapIconButton
                ariaLabel="Pan up"
                className="MapCameraControlDpad__up"
                onClick={() => pan(0, -PAN_STEP_PX)}
              >
                <ArrowIcon direction="up" />
              </MapIconButton>
              <MapIconButton
                ariaLabel="Pan left"
                className="MapCameraControlDpad__left"
                onClick={() => pan(-PAN_STEP_PX, 0)}
              >
                <ArrowIcon direction="left" />
              </MapIconButton>
              <MapIconButton
                ariaLabel="Pan right"
                className="MapCameraControlDpad__right"
                onClick={() => pan(PAN_STEP_PX, 0)}
              >
                <ArrowIcon direction="right" />
              </MapIconButton>
              <MapIconButton
                ariaLabel="Pan down"
                className="MapCameraControlDpad__down"
                onClick={() => pan(0, PAN_STEP_PX)}
              >
                <ArrowIcon direction="down" />
              </MapIconButton>
            </div>
            <div className="MapCameraControlZoom">
              <MapIconButton ariaLabel="Zoom in" onClick={() => zoomBy(1)}>
                <PlusIcon />
              </MapIconButton>
              <MapIconButton ariaLabel="Zoom out" onClick={() => zoomBy(-1)}>
                <MinusIcon />
              </MapIconButton>
            </div>
          </div>
        )}
        <MapIconButton
          onClick={() => setExpanded((value) => !value)}
          ariaLabel="Map navigation controls"
          className="MapControlButton--camera"
        >
          <PanIcon />
        </MapIconButton>
      </div>
    </MapControl>
  );
};
